// pages/superadmin/FormSubmissions.jsx
// Dynamic client-facing brief-form submissions (formSchemaController). Each
// row is a BriefFormSubmission, the client's raw answers + uploads, kept
// independently of whether the downstream Duda build succeeded. When Duda
// errors, the row stays here with dudaStatus="failed" so it can be retried
// from a button click instead of asking the client to refill the form.
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import apiService from "../../services/api";
import NotificationBell from "../../components/NotificationBell";
import { useAuth } from "../../contexts/AuthContext";
import { Dialog, DialogContent } from "../../arc/components/dialog/dialog";
import { SearchField } from "../../arc/components/search-field/search-field";
import { Input } from "../../arc/components/input/input";
import { Skeleton } from "../../arc/components/skeleton/skeleton";
import { ThemeSwitch } from "../../arc/components/theme-switch/theme-switch";
import { applyThemeWithTransition } from "../../utils/themeTransition";

const API_URL = process.env.REACT_APP_API_URL;

const styles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .dash-root.dark .dash-header { box-shadow: 0 1px 0 rgba(255,255,255,0.03), 0 2px 20px rgba(0,0,0,0.6); }

  .dash-root { min-height: 100vh; background: var(--bg); color: var(--text); font-family: var(--sans); font-size: 14px; line-height: 1.5; -webkit-font-smoothing: antialiased; }

  /* HEADER, matches QAQueue/CustomersManagement header */
  .dash-header { background: #082a58; border-bottom: 1px solid rgba(255,255,255,0.07);  position: sticky; top: 0; z-index: 100; }
  .dash-header-inner { max-width: 1560px; margin: 0 auto; padding: 0 28px; height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
  .dash-brand { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }

  .btn-back { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: var(--radius-sm); color: rgba(255,255,255,0.85); transition: all var(--t); margin-right: 4px; }
  .btn-back:hover { background: rgba(255,255,255,0.13); border-color: rgba(255,255,255,0.22); color: #fff; transform: translateX(-2px); }

  .dash-brand-text { display: flex; flex-direction: column; gap: 1px; }
  .dash-brand-name { font-size: 13.5px; font-weight: 600; color: #fff; letter-spacing: -0.01em; }
  .dash-brand-sub { font-family: var(--mono); font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; color: rgba(255,255,255,0.38); }
  .dash-header-right { display: flex; align-items: center; gap: 8px; }

  .btn-header { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 36px; padding: 0 14px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; font-family: var(--sans); font-size: 12.5px; font-weight: 500; color: rgba(255,255,255,0.78); cursor: pointer; text-decoration: none; transition: all var(--t); }
  .btn-header:hover { background: rgba(255,255,255,0.13); border-color: rgba(255,255,255,0.22); color: #fff; }

  .user-pill { display: flex; align-items: center; gap: 7px; padding: 5px 12px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: 100px; font-size: 12.5px; font-weight: 500; color: rgba(255,255,255,0.85); }
  .online-dot { width: 6px; height: 6px; border-radius: 50%; background: #34d399; box-shadow: 0 0 0 2px rgba(52,211,153,0.25); flex-shrink: 0; }

  /* SETTINGS */
  .settings-wrap { position: relative; }
  .settings-panel { display: none; position: absolute; right: 0; top: calc(100% + 8px); min-width: 210px; background: #0d2d5a; border: 1px solid rgba(255,255,255,0.1); border-radius: var(--radius); box-shadow: 0 12px 40px rgba(0,0,0,0.5); z-index: 200; padding: 4px; }
  .settings-panel.open { display: block; }
  .dash-root.dark .settings-panel { background: #1a1a24; border-color: var(--border); }
  .settings-row { display: flex; align-items: center; justify-content: space-between; padding: 9px 12px; font-size: 12.5px; color: rgba(255,255,255,0.7); border-radius: var(--radius-sm); cursor: pointer; transition: background var(--t); }
  .settings-row:hover { background: rgba(255,255,255,0.07); color: #fff; }

  /* TOGGLE */
  .toggle { position: relative; width: 32px; height: 18px; flex-shrink: 0; }
  .toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
  .toggle-track { position: absolute; inset: 0; background: rgba(255,255,255,0.2); border-radius: 100px; cursor: pointer; transition: background var(--t); }
  .toggle-track::after { content: ''; position: absolute; width: 14px; height: 14px; left: 2px; top: 2px; background: #fff; border-radius: 50%; box-shadow: 0 1px 4px rgba(0,0,0,0.25); transition: transform 0.2s var(--ease); }
  .toggle input:checked ~ .toggle-track { background: #34d399; }
  .toggle input:checked ~ .toggle-track::after { transform: translateX(14px); }

  /* MAIN */
  .dash-main { max-width: 1560px; margin: 0 auto; padding: 28px; display: flex; flex-direction: column; gap: 20px; }

  /* SEARCH */
  .search-wrap { position: relative; flex: 1; max-width: 400px; }
  .search-input { width: 100%; padding: 8px 14px 8px 36px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text); font-family: var(--sans); font-size: 13px; outline: none; transition: border-color var(--t), box-shadow var(--t); }
  .search-input::placeholder { color: var(--text-faint); }
  .search-input:focus { border-color: var(--accent-mid); box-shadow: 0 0 0 3px var(--accent-dim); }
  .search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-faint); pointer-events: none; }
  .search-clear { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; color: var(--text-faint); cursor: pointer; padding: 4px; display: flex; align-items: center; justify-content: center; border-radius: 4px; transition: all var(--t); }
  .search-clear:hover { color: var(--text-muted); background: var(--surface-hover); }

  /* BUTTONS */
  .btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 36px; padding: 0 16px; border: 1px solid transparent; border-radius: 8px; font-family: var(--sans); font-size: 12.5px; font-weight: 500; line-height: 1.45; cursor: pointer; white-space: nowrap; text-decoration: none; outline: none; -webkit-tap-highlight-color: transparent; transition: color var(--t), background var(--t), border-color var(--t), box-shadow var(--t), opacity var(--t); }
  .btn:focus-visible { box-shadow: 0 0 0 3px var(--accent-dim); }
  .btn:disabled { opacity: 0.52; cursor: not-allowed; }
  .btn-sm { min-height: 32px; padding: 0 12px; }
  .btn-md { min-height: 36px; padding: 0 16px; }
  .btn-lg { min-height: 40px; padding: 0 20px; }
  .btn-ghost { background: transparent; border: 1px solid var(--border); color: var(--text-muted); }
  .btn-ghost:hover { background: var(--surface-raised); border-color: var(--border-strong); color: var(--text); }
  .btn-success-ghost { background: var(--green-dim); border: 1px solid var(--green-border); color: var(--green); }
  .btn-success-ghost:hover { background: var(--green); color: #fff; }
  .btn-success-ghost:disabled { opacity: 0.5; cursor: not-allowed; background: var(--green-dim); color: var(--green); }
  .btn-danger-ghost { background: var(--red-dim); border: 1px solid var(--red-border); color: var(--red); }
  .btn-danger-ghost:hover { background: var(--red); color: #fff; }
  .btn-danger-ghost:disabled { opacity: 0.5; cursor: not-allowed; }

  /* TABS */
  .qa-tabs { display: flex; gap: 6px; flex-wrap: wrap; }
  .qa-tab { padding: 6px 14px; border-radius: var(--radius-sm); border: 1px solid var(--border); background: var(--surface); font-family: var(--sans); font-size: 12.5px; font-weight: 500; cursor: pointer; color: var(--text-muted); transition: all var(--t); }
  .qa-tab:hover { border-color: var(--border-strong); color: var(--text); }
  .qa-tab.active { background: var(--accent); color: #fff; border-color: var(--accent); }

  /* PANEL / LIST */
  .table-wrap { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); box-shadow: var(--shadow-sm); overflow: hidden; }
  .qa-list { display: flex; flex-direction: column; }
  .qa-card { padding: 16px 18px; display: flex; align-items: center; justify-content: space-between; gap: 16px; border-bottom: 1px solid var(--border); transition: background-color 0.12s ease; }
  .qa-card:last-child { border-bottom: none; }
  .qa-card:hover { background-color: var(--surface-hover); }
  .qa-card-info { min-width: 0; flex: 1; }
  .qa-card-name { font-size: 13.5px; font-weight: 600; color: var(--text); letter-spacing: -0.02em; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .qa-card-company-id { font-family: var(--mono); font-size: 11px; font-weight: 400; color: var(--text-faint); background: var(--surface-sub); padding: 1px 8px; border-radius: var(--radius-xs); border: 1px solid var(--border); }
  .qa-card-sub { font-family: var(--mono); font-size: 11px; color: var(--text-faint); margin-top: 3px; }
  .qa-card-actions { display: flex; gap: 8px; flex-shrink: 0; flex-wrap: wrap; }
  .qa-notes-box { margin-top: 8px; padding: 8px 12px; background: var(--amber-dim); border: 1px solid var(--amber-border); border-radius: var(--radius-sm); font-size: 12px; color: var(--amber); }

  /* STATUS PILL */
  .status-pill { display: inline-flex; align-items: center; gap: 5px; padding: 2px 9px; border-radius: 100px; font-family: var(--mono); font-size: 10.5px; font-weight: 500; letter-spacing: 0.03em; text-transform: uppercase; }
  .status-pill::before { content: ''; width: 5px; height: 5px; border-radius: 50%; }
  .status-pending { background: var(--surface-sub); color: var(--text-faint); border: 1px solid var(--border); }
  .status-pending::before { background: var(--text-faint); }
  .status-building { background: var(--accent-dim); color: var(--accent-light); border: 1px solid var(--accent-glow); }
  .status-building::before { background: var(--accent-light); }
  .status-ready { background: var(--green-dim); color: var(--green); border: 1px solid var(--green-border); }
  .status-ready::before { background: var(--green); }
  .status-failed { background: var(--red-dim); color: var(--red); border: 1px solid var(--red-border); }
  .status-failed::before { background: var(--red); }

  /* TABLE TOOLBAR */
  .table-toolbar { display: flex; align-items: center; justify-content: space-between; padding: 8px 14px; border-bottom: 1px solid var(--border); background: var(--surface-raised); gap: 10px; flex-wrap: wrap; }
  .toolbar-label { font-family: var(--mono); font-size: 10.5px; color: var(--text-faint); letter-spacing: 0.04em; }

  /* PAGINATION */
  .pagination { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; border-top: 1px solid var(--border); background: var(--surface-raised); gap: 10px; flex-wrap: wrap; }
  .page-info { font-family: var(--mono); font-size: 10.5px; color: var(--text-faint); letter-spacing: 0.04em; }
  .page-info b { color: var(--text-muted); font-weight: 500; }
  .page-controls { display: flex; align-items: center; gap: 2px; flex-wrap: wrap; }
  .btn-page { display: flex; align-items: center; justify-content: center; min-width: 28px; height: 28px; padding: 0 7px; background: transparent; border: 1px solid var(--border); color: var(--text-muted); border-radius: var(--radius-sm); font-family: var(--mono); font-size: 11px; cursor: pointer; transition: all var(--t); }
  .btn-page:hover:not(:disabled) { border-color: var(--accent-mid); color: var(--accent-mid); background: var(--accent-dim); }
  .btn-page:disabled { opacity: 0.25; cursor: not-allowed; }
  .btn-page.active { background: var(--accent); border-color: var(--accent); color: #fff; font-weight: 600; }
  .page-ellipsis { display: inline-flex; align-items: center; justify-content: center; width: 22px; color: var(--text-faint); font-size: 11px; }
  .page-jump { display: flex; align-items: center; gap: 6px; font-family: var(--mono); font-size: 10.5px; color: var(--text-faint); }
  .page-jump-input { width: 44px; height: 28px; padding: 0 7px; background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text); font-family: var(--mono); font-size: 10.5px; text-align: center; outline: none; transition: border-color var(--t), box-shadow var(--t); }
  .page-jump-input:focus { border-color: var(--accent-mid); box-shadow: 0 0 0 2px var(--accent-dim); }

  /* EMPTY / LOADING */
  .empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 72px 20px; gap: 12px; text-align: center; }
  .empty-state-label { font-family: var(--mono); font-size: 11px; letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-faint); }
  .spinner { width: 28px; height: 28px; border: 2px solid var(--border); border-top-color: var(--accent-mid); border-radius: 50%; animation: spin 0.7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* MODAL, used for the error detail / answers-preview view */
  .qa-modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 400; display: flex; align-items: center; justify-content: center; padding: 20px; }
  .qa-modal { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 28px; max-width: 700px; width: 100%; box-shadow: var(--shadow-modal); max-height: 90vh; overflow-y: auto; }
  .qa-modal-title { font-size: 16px; font-weight: 600; color: var(--text); margin-bottom: 6px; letter-spacing: -0.02em; }
  .qa-modal-sub { font-size: 13px; color: var(--text-muted); line-height: 1.6; margin-bottom: 16px; }
  .qa-modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }
  .fs-detail-dialog { width: min(calc(100vw - var(--space-8)), 720px); }
  .error-box { padding: 10px 12px; background: var(--red-dim); border: 1px solid var(--red-border); border-radius: var(--radius-sm); font-family: var(--mono); font-size: 12px; color: var(--red); white-space: pre-wrap; word-break: break-word; margin-bottom: 14px; }
  .meta-row { display: flex; justify-content: space-between; padding: 7px 0; border-bottom: 1px solid var(--border); font-size: 12.5px; }
  .meta-row:last-child { border-bottom: none; }
  .meta-row-label { color: var(--text-faint); font-family: var(--mono); font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.04em; }
  .meta-row-value { color: var(--text); font-weight: 500; text-align: right; }

  /* ===== ENHANCED ANSWER DISPLAY STYLES ===== */
  .answers-full-section {
    margin: 14px 0;
    padding: 0 0 8px 0;
    max-height: 450px;
    overflow-y: auto;
  }

  .answers-full-header {
    padding: 6px 0 10px 0;
    border-bottom: 1px solid var(--border);
    margin-bottom: 12px;
  }

  .answer-category {
    margin-bottom: 14px;
  }

  .answer-category-title {
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--text-faint);
    padding: 4px 0 6px 0;
    border-bottom: 1px dashed var(--border);
    margin-bottom: 6px;
  }

  .answer-item-full {
    display: grid;
    grid-template-columns: 150px 1fr;
    gap: 10px;
    padding: 4px 0;
    border-bottom: 1px solid var(--border);
    font-size: 12.5px;
  }

  .answer-item-full:last-child {
    border-bottom: none;
  }

  .answer-item-full-key {
    color: var(--text-muted);
    font-weight: 500;
    font-size: 11.5px;
    padding-right: 8px;
    word-break: break-word;
  }

  .answer-item-full-value {
    color: var(--text);
    word-break: break-word;
    line-height: 1.4;
  }

  .answer-item-full-value.empty {
    color: var(--text-faint);
    font-style: italic;
  }

  .answer-item-full-value.array-value {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  .array-tag {
    display: inline-block;
    padding: 1px 8px;
    background: var(--accent-dim);
    border-radius: 12px;
    font-size: 11px;
    color: var(--accent-light);
    border: 1px solid var(--accent-glow);
  }

  .array-tag.dark {
    background: var(--accent-dim);
    color: var(--accent-light);
  }

  .typo-slider-indicator {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: var(--mono);
    font-size: 11px;
    color: var(--text-muted);
  }

  .typo-slider-track {
    width: 100px;
    height: 4px;
    background: var(--border);
    border-radius: 4px;
    position: relative;
  }

  .typo-slider-fill {
    height: 4px;
    background: var(--accent-mid);
    border-radius: 4px;
    transition: width 0.3s ease;
  }

  .typo-slider-label {
    font-size: 10px;
    color: var(--text-faint);
  }

  .typo-slider-value {
    font-weight: 600;
    color: var(--accent-light);
    min-width: 28px;
    text-align: center;
  }

  /* Uploads */
  .uploads-section { margin: 14px 0; padding-bottom: 14px; border-bottom: 1px solid var(--border); }
  .uploads-grid { display: flex; flex-wrap: wrap; gap: 8px; }
  .upload-thumb { display: flex; flex-direction: column; align-items: center; gap: 4px; width: 64px; text-decoration: none; }
  .upload-thumb img { width: 64px; height: 64px; object-fit: cover; border-radius: var(--radius-sm); border: 1px solid var(--border); background: var(--surface-sub); }
  .upload-thumb-file { width: 64px; height: 64px; display: flex; align-items: center; justify-content: center; border-radius: var(--radius-sm); border: 1px solid var(--border); background: var(--surface-sub); color: var(--text-faint); }
  .upload-thumb-label { font-family: var(--mono); font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-faint); text-align: center; max-width: 64px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .upload-thumb:hover img, .upload-thumb:hover .upload-thumb-file { border-color: var(--accent-mid); }

  /* RESPONSIVE */
  @media (max-width: 768px) {
    .dash-header-inner { padding: 0 16px; }
    .dash-main { padding: 16px; }
    .qa-card { flex-direction: column; align-items: flex-start; gap: 12px; }
    .qa-card-actions { width: 100%; justify-content: flex-start; }
    .pagination { flex-direction: column; align-items: stretch; }
    .pagination .page-controls { justify-content: center; }
    .search-wrap { max-width: 100%; }
    .qa-tabs { width: 100%; }
    .qa-modal { padding: 16px; max-height: 95vh; }
    .answer-item-full {
      grid-template-columns: 1fr;
      gap: 2px;
      padding: 6px 0;
    }
    .answer-item-full-key {
      font-size: 10px;
      color: var(--text-faint);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .answers-full-section {
      max-height: 300px;
    }
  }

  @media (max-width: 480px) {
    .qa-card-name { font-size: 12px; flex-wrap: wrap; }
    .qa-card-sub { font-size: 10px; }
    .qa-card-actions { gap: 4px; }
    .btn-sm { min-height: 30px; padding: 0 10px; }
    .meta-row { flex-direction: column; align-items: flex-start; gap: 2px; }
    .meta-row-value { text-align: left; }
  }
`;

const TABS = [
  { key: "", label: "All" },
  { key: "failed", label: "Failed" },
  { key: "building", label: "Building" },
  { key: "ready", label: "Ready" },
  { key: "pending", label: "Pending" },
];

const PAGE_SIZE = 25;

// ===== HELPERS FOR ANSWER DISPLAY =====

function formatAnswerValue(value) {
  if (value === null || value === undefined) return "-";
  if (Array.isArray(value)) {
    if (value.length === 0) return "-";
    return value;
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function getAnswerLabel(key) {
  // Convert AXO_XXX to human-readable label
  return key
    .replace(/^AXO_/, "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isSliderField(key) {
  return [
    "AXO_TYPOGRAPHY_TRADITIONAL_MODERN",
    "AXO_TYPOGRAPHY_ELEGANT_PLAYFUL",
    "AXO_TYPOGRAPHY_MINIMAL_DECORATIVE",
  ].includes(key);
}

function getSliderLabel(key) {
  const labels = {
    AXO_TYPOGRAPHY_TRADITIONAL_MODERN: { left: "Traditional", right: "Modern" },
    AXO_TYPOGRAPHY_ELEGANT_PLAYFUL: { left: "Elegant", right: "Playful" },
    AXO_TYPOGRAPHY_MINIMAL_DECORATIVE: { left: "Minimal", right: "Decorative" },
  };
  return labels[key] || { left: "0", right: "100" };
}

function renderAnswerValue(key, value) {
  const formatted = formatAnswerValue(value);

  if (value === null || value === undefined || value === "") {
    return <span className="answer-item-full-value empty">Not provided</span>;
  }

  // Handle slider fields with visual indicator
  if (isSliderField(key) && typeof value === "number") {
    const labels = getSliderLabel(key);
    return (
      <div className="typo-slider-indicator">
        <span className="typo-slider-label">{labels.left}</span>
        <div className="typo-slider-track">
          <div className="typo-slider-fill" style={{ width: `${value}%` }} />
        </div>
        <span className="typo-slider-label">{labels.right}</span>
        <span className="typo-slider-value">{value}</span>
      </div>
    );
  }

  // Handle array values as tags
  if (Array.isArray(value) && value.length > 0) {
    return (
      <div className="answer-item-full-value array-value">
        {value.map((item, idx) => (
          <span key={idx} className="array-tag">
            {String(item)}
          </span>
        ))}
      </div>
    );
  }

  // Handle long text with line breaks
  if (typeof value === "string" && value.length > 80) {
    return (
      <div
        className="answer-item-full-value"
        style={{ whiteSpace: "pre-wrap" }}
      >
        {value}
      </div>
    );
  }

  return <div className="answer-item-full-value">{formatted}</div>;
}

function categorizeAnswers(answers) {
  if (!answers || typeof answers !== "object") return {};

  const categories = {
    "Business Information": [],
    Branding: [],
    "Look & Feel": [],
    "About Your Business": [],
    "Products & Services": [],
    Customers: [],
    Differentiation: [],
    "Website Requirements": [],
    SEO: [],
    "AI Content": [],
    Other: [],
  };

  const categoryMap = {
    // Business Info
    AXO_TRADING_NAME: "Business Information",
    AXO_COMPANY_REG_NUMBER: "Business Information",
    AXO_VAT_NUMBER: "Business Information",
    AXO_INDUSTRY: "Business Information",
    AXO_BUSINESS_TYPE: "Business Information",
    AXO_YEAR_ESTABLISHED: "Business Information",
    AXO_NUMBER_OF_EMPLOYEES: "Business Information",
    AXO_PRIMARY_LOCATION: "Business Information",
    AXO_AREAS_COVERED: "Business Information",
    AXO_SHORT_DESCRIPTION: "Business Information",
    AXO_NAME_PRONUNCIATION: "Business Information",
    AXO_TAGLINE: "Business Information",
    AXO_EXISTING_WEBSITE_SOCIAL: "Business Information",

    // Branding
    AXO_PRIMARY_BRAND_COLOUR: "Branding",
    AXO_SECONDARY_BRAND_COLOUR: "Branding",

    // Look & Feel
    AXO_ADMIRED_SITES: "Look & Feel",
    AXO_SITES_TO_AVOID: "Look & Feel",
    AXO_TYPOGRAPHY_TRADITIONAL_MODERN: "Look & Feel",
    AXO_TYPOGRAPHY_ELEGANT_PLAYFUL: "Look & Feel",
    AXO_TYPOGRAPHY_MINIMAL_DECORATIVE: "Look & Feel",
    AXO_IMAGERY_STYLE: "Look & Feel",
    AXO_MOOD_WORDS: "Look & Feel",
    AXO_DENSITY: "Look & Feel",
    AXO_PREMIUM_DEFINITION: "Look & Feel",

    // About
    AXO_KEY_MESSAGE: "About Your Business",
    AXO_FIRST_IMPRESSION: "About Your Business",
    AXO_WHAT_MAKES_UNIQUE: "About Your Business",
    AXO_CORE_VALUES: "About Your Business",
    AXO_WHY_CUSTOMERS_CHOOSE_YOU: "About Your Business",
    AXO_PROBLEMS_SOLVED: "About Your Business",
    AXO_WHY_STARTED: "About Your Business",
    AXO_PLAIN_DESCRIPTION: "About Your Business",
    AXO_ABOUT_LONG: "About Your Business",

    // Products & Services
    AXO_SIGNATURE_OFFER: "Products & Services",
    AXO_NOT_OFFERED: "Products & Services",
    services: "Products & Services",

    // Customers
    AXO_IDEAL_CUSTOMER_TYPE: "Customers",
    AXO_TYPICAL_CUSTOMER: "Customers",
    AXO_INDUSTRIES_SERVED: "Customers",
    AXO_AVERAGE_CUSTOMER_SIZE: "Customers",
    AXO_CUSTOMER_GEOGRAPHIC_AREAS: "Customers",
    AXO_CUSTOMER_OWN_WORDS: "Customers",
    AXO_CUSTOMER_TRIGGER: "Customers",
    AXO_CUSTOMER_HESITATION: "Customers",
    AXO_CUSTOMER_PREVIOUS_SOLUTION: "Customers",
    AXO_CUSTOMER_QUOTE: "Customers",
    AXO_BIGGEST_CUSTOMER_CHALLENGES: "Customers",
    AXO_PERFECT_CUSTOMER_DESC: "Customers",

    // Differentiation
    AXO_TOP_REASONS: "Differentiation",
    AXO_TOP_REASONS_PROOF: "Differentiation",
    AXO_WHY_BETTER_THAN_COMPETITORS: "Differentiation",
    AXO_TESTIMONIALS: "Differentiation",
    AXO_NAMED_CLIENTS: "Differentiation",
    AXO_FIGURES_RESULTS: "Differentiation",
    AXO_AWARDS_CERTS: "Differentiation",
    AXO_GUARANTEES: "Differentiation",
    AXO_PRESS_FEATURES: "Differentiation",
    competitorUrls: "Differentiation",

    // Website
    AXO_PAGES_REQUIRED: "Website Requirements",
    AXO_FEATURES_REQUIRED: "Website Requirements",
    AXO_PRIMARY_ACTION: "Website Requirements",
    AXO_SUCCESS_DEFINITION: "Website Requirements",
    AXO_MOST_IMPORTANT_PAGE: "Website Requirements",
    AXO_CONTENT_AVAILABLE: "Website Requirements",
    AXO_CONTENT_NEEDED: "Website Requirements",
    AXO_CALL_TO_ACTION: "Website Requirements",

    // SEO
    AXO_SEO_PRIMARY_KEYWORDS: "SEO",
    AXO_SEO_SECONDARY_KEYWORDS: "SEO",
    AXO_SEO_LOCATIONS: "SEO",
    AXO_CUSTOMER_QUESTIONS: "SEO",

    // AI Content
    AXO_ONE_SENTENCE_PITCH: "AI Content",
    AXO_THREE_SENTENCE_PITCH: "AI Content",
    AXO_VOICE_SAMPLE: "AI Content",
    AXO_VOICE_CONTRAST: "AI Content",
    AXO_BRAND_PERSONALITY: "AI Content",
    AXO_TONE_OF_VOICE: "AI Content",
    AXO_PREFERRED_LINES: "AI Content",
    AXO_WORDS_TO_AVOID: "AI Content",
    AXO_CUSTOMER_OWN_WORDS: "AI Content",
  };

  // Special handling: services array
  const services = answers.services || [];
  if (services.length > 0) {
    const serviceNames = services.map((s) => {
      if (typeof s === "string") return s;
      return s.name || s.title || "Unnamed service";
    });
    categories["Products & Services"].push({
      key: "services",
      label: "Services Offered",
      value: serviceNames,
    });
  }

  // Special handling: competitorUrls array
  const competitors = answers.competitorUrls || [];
  if (competitors.length > 0) {
    categories["Differentiation"].push({
      key: "competitorUrls",
      label: "Competitor URLs",
      value: competitors,
    });
  }

  // Categorize all other answers
  for (const [key, value] of Object.entries(answers)) {
    if (key === "services" || key === "competitorUrls") continue;
    if (value === null || value === undefined) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    if (Array.isArray(value) && value.length === 0) continue;

    const category = categoryMap[key] || "Other";
    categories[category].push({
      key,
      label: getAnswerLabel(key),
      value: value,
    });
  }

  // Remove empty categories
  return Object.fromEntries(
    Object.entries(categories).filter(([_, items]) => items.length > 0),
  );
}

// ===== COMPONENT =====

function StatusPill({ status }) {
  return <span className={`status-pill status-${status}`}>{status}</span>;
}

export default function FormSubmissions() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("failed");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("axo_theme") === "dark",
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDebounce, setSearchDebounce] = useState("");

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
  });
  const [jumpPage, setJumpPage] = useState("");

  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("axo_theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  useEffect(() => {
    if (!settingsOpen) return;
    const handler = () => setSettingsOpen(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [settingsOpen]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getFormSubmissions({
        dudaStatus: tab || undefined,
        search: searchDebounce || undefined,
        page: pagination.currentPage,
        limit: PAGE_SIZE,
      });

      const data = res?.data || [];
      const total = res?.pagination?.total ?? data.length;
      const currentPage = res?.pagination?.page || 1;
      const totalPages =
        res?.pagination?.totalPages || Math.ceil(total / PAGE_SIZE) || 1;

      setRows(data);
      setPagination({ currentPage, totalPages, total });
    } catch (err) {
      console.error("Failed to load form submissions:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load form submissions",
      );
    } finally {
      setLoading(false);
    }
  }, [tab, pagination.currentPage, searchDebounce]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [tab, searchDebounce]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearchDebounce(value.trim());
    }, 500);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchDebounce("");
    clearTimeout(searchTimeoutRef.current);
  };

  const openDetail = async (row) => {
    setDetailTarget(row);
    setDetailLoading(true);
    try {
      const res = await apiService.getFormSubmission(row.id);
      setDetailTarget(res?.data || row);
    } catch (err) {
      console.error("Failed to load submission detail:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRetry = async (id) => {
    setBusyId(id);
    setError(null);
    try {
      const res = await apiService.retryFormSubmissionDuda(id);
      const updated = res?.data;
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, ...updated } : r)),
      );
      if (detailTarget?.id === id) {
        setDetailTarget(updated);
      }
    } catch (err) {
      console.error("Failed to retry Duda build:", err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.detail ||
          err.message ||
          "Retry failed",
      );
    } finally {
      setBusyId(null);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleJumpPage = () => {
    const page = parseInt(jumpPage, 10);
    if (!isNaN(page) && page >= 1 && page <= pagination.totalPages) {
      handlePageChange(page);
      setJumpPage("");
    }
  };

  const getPageNumbers = () => {
    const { currentPage: c, totalPages: t } = pagination;
    if (t <= 7) return Array.from({ length: t }, (_, i) => i + 1);
    if (c <= 4) return [1, 2, 3, 4, 5, "...", t];
    if (c >= t - 3) return [1, "...", t - 4, t - 3, t - 2, t - 1, t];
    return [1, "...", c - 1, c, c + 1, "...", t];
  };

  const formatDate = (d) => {
    if (!d) return "-";
    try {
      return new Date(d).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return d;
    }
  };

  const uploadUrl = (id) => `${API_URL}/briefs/upload/${id}`;
  const isImageUpload = (u) => (u.mimeType || "").startsWith("image/");

  const getCompanyName = (row) =>
    row.companyNewco?.companyName ||
    row.answers?.AXO_TRADING_NAME ||
    "Unnamed company";

  const getContactEmail = (row) => row.contact?.email || null;
  const getContactPhone = (row) => row.contact?.phone || null;

  const summarizeAnswers = (answers = {}) => {
    const trading = answers.AXO_TRADING_NAME;
    const industry = answers.AXO_INDUSTRY;
    return (
      [trading, industry].filter(Boolean).join(" · ") ||
      "No trading name on file"
    );
  };

  const answerCount = (answers = {}) => {
    if (!answers) return 0;
    let count = 0;
    for (const [key, value] of Object.entries(answers)) {
      if (key === "services" || key === "competitorUrls") continue;
      if (value === null || value === undefined) continue;
      if (typeof value === "string" && value.trim() === "") continue;
      if (Array.isArray(value) && value.length === 0) continue;
      count++;
    }
    // Add services and competitors if they have values
    if (answers.services?.length) count++;
    if (answers.competitorUrls?.length) count++;
    return count;
  };

  return (
    <div className={`dash-root${darkMode ? " dark" : ""}`}>
      <style>{styles}</style>

      <header className="dash-header">
        <div className="dash-header-inner">
          <div className="dash-brand">
            <Link
              to="/super-admin"
              className="btn-back"
              title="Back to Dashboard"
            >
              <svg
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </Link>
            <div className="dash-brand-text">
              <div className="dash-brand-name">Form Submissions</div>
              <div className="dash-brand-sub">Axo Signal</div>
            </div>
          </div>

          <div className="dash-header-right">
            <NotificationBell />
            <div className="settings-wrap" onClick={(e) => e.stopPropagation()}>
              <button
                className="btn-header"
                onClick={() => setSettingsOpen((o) => !o)}
              >
                <svg
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                Settings
              </button>
              <div className={`settings-panel${settingsOpen ? " open" : ""}`}>
                <div className="settings-row">
                  <span>Dark mode</span>
                  <ThemeSwitch
                    iconOnly
                    theme={darkMode ? "dark" : "light"}
                    onThemeChange={(next, variant, trigger) => applyThemeWithTransition(next, trigger, setDarkMode, variant)}
                  />
                </div>
              </div>
            </div>
            <div className="user-pill">
              <div className="online-dot" />
              {user?.name}
            </div>
            <button onClick={logout} className="btn-header">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="dash-main">
        <div
          className="page-heading-row"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <div className="qa-tabs">
            {TABS.map((t) => (
              <button
                key={t.key || "all"}
                className={`qa-tab ${tab === t.key ? "active" : ""}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
                {t.key === tab && pagination.total > 0 && (
                  <span
                    style={{
                      marginLeft: "6px",
                      fontSize: "10px",
                      opacity: 0.6,
                      fontFamily: "var(--mono)",
                    }}
                  >
                    ({pagination.total})
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="search-wrap">
            <SearchField
              label="Search"
              hideLabel
              value={searchQuery}
              onValueChange={handleSearchChange}
              placeholder="Search by company name or ID..."
            />
          </div>
        </div>

        {error && <div className="qa-notes-box">{error}</div>}

        <div className="table-wrap">
          {!loading && rows.length > 0 && (
            <div className="table-toolbar">
              <span className="toolbar-label">
                {pagination.total.toLocaleString()} results
              </span>
            </div>
          )}

          {loading ? (
            <div className="empty-state" style={{ width: "100%" }}>
              <Skeleton lines={6} label="Loading form submissions" />
            </div>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-label">
                {searchDebounce
                  ? `No results found for "${searchDebounce}"`
                  : "Nothing here right now"}
              </div>
            </div>
          ) : (
            <>
              <div className="qa-list">
                {rows.map((row) => (
                  <div className="qa-card" key={row.id}>
                    <div className="qa-card-info">
                      <div className="qa-card-name">
                        <span>{getCompanyName(row)}</span>
                        <StatusPill status={row.dudaStatus} />
                        <span className="qa-card-company-id">
                          #{row.companyId}
                        </span>
                        {row.attempts > 0 && (
                          <span
                            style={{
                              fontFamily: "var(--mono)",
                              fontSize: "10.5px",
                              color: "var(--text-faint)",
                            }}
                          >
                            {row.attempts} attempt
                            {row.attempts === 1 ? "" : "s"}
                          </span>
                        )}
                        {answerCount(row.answers) > 0 && (
                          <span
                            style={{
                              fontFamily: "var(--mono)",
                              fontSize: "10.5px",
                              color: "var(--text-muted)",
                              background: "var(--surface-sub)",
                              padding: "1px 8px",
                              borderRadius: "var(--radius-xs)",
                              border: "1px solid var(--border)",
                            }}
                          >
                            {answerCount(row.answers)} fields
                          </span>
                        )}
                      </div>
                      <div className="qa-card-sub">
                        Submitted {formatDate(row.createdAt)}
                        {row.lastAttemptAt
                          ? ` · last attempt ${formatDate(row.lastAttemptAt)}`
                          : ""}
                        {getContactEmail(row)
                          ? ` · ${getContactEmail(row)}`
                          : ""}
                        {!getContactEmail(row) && getContactPhone(row)
                          ? ` · ${getContactPhone(row)}`
                          : ""}
                        {row.customer?.name
                          ? ` · Customer: ${row.customer.name}`
                          : ""}
                      </div>
                      {row.dudaStatus === "failed" && row.dudaError && (
                        <div className="qa-notes-box">
                          {row.dudaError.length > 140
                            ? `${row.dudaError.slice(0, 140)}…`
                            : row.dudaError}
                        </div>
                      )}
                    </div>
                    <div className="qa-card-actions">
                      {row.websiteUrl && (
                        <a
                          className="btn btn-ghost btn-sm"
                          href={row.websiteUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View website
                        </a>
                      )}
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => openDetail(row)}
                      >
                        Details
                      </button>
                      {row.dudaStatus !== "building" && (
                        <button
                          className="btn btn-success-ghost btn-sm"
                          disabled={busyId === row.id}
                          onClick={() => handleRetry(row.id)}
                        >
                          {busyId === row.id
                            ? "Retrying…"
                            : row.dudaStatus === "failed"
                              ? "Retry build"
                              : "Rebuild"}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {pagination.totalPages > 1 && (
                <div className="pagination">
                  <div className="page-info">
                    Page <b>{pagination.currentPage}</b> of{" "}
                    <b>{pagination.totalPages}</b>&ensp;·&ensp;
                    {pagination.total.toLocaleString()} total
                  </div>
                  <div className="page-controls">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={pagination.currentPage === 1}
                      className="btn-page"
                      title="First"
                    >
                      «
                    </button>
                    <button
                      onClick={() =>
                        handlePageChange(pagination.currentPage - 1)
                      }
                      disabled={pagination.currentPage === 1}
                      className="btn-page"
                      title="Previous"
                    >
                      ‹
                    </button>
                    {getPageNumbers().map((page, i) =>
                      page === "..." ? (
                        <span key={`e${i}`} className="page-ellipsis">
                          …
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`btn-page${pagination.currentPage === page ? " active" : ""}`}
                        >
                          {page}
                        </button>
                      ),
                    )}
                    <button
                      onClick={() =>
                        handlePageChange(pagination.currentPage + 1)
                      }
                      disabled={
                        pagination.currentPage === pagination.totalPages
                      }
                      className="btn-page"
                      title="Next"
                    >
                      ›
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.totalPages)}
                      disabled={
                        pagination.currentPage === pagination.totalPages
                      }
                      className="btn-page"
                      title="Last"
                    >
                      »
                    </button>
                  </div>
                  <div className="page-jump">
                    Go to
                    <Input
                      label="Go to page"
                      hideLabel
                      type="number"
                      min={1}
                      max={pagination.totalPages}
                      value={jumpPage}
                      onChange={(e) => setJumpPage(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleJumpPage()}
                      className="page-jump-input"
                      placeholder="-"
                    />
                    <button
                      onClick={handleJumpPage}
                      className="btn btn-ghost btn-sm"
                    >
                      Go
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ===== DETAIL MODAL WITH FULL ANSWERS ===== */}
      {detailTarget && (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next) setDetailTarget(null);
          }}
        >
          <DialogContent
            className="fs-detail-dialog"
            title={getCompanyName(detailTarget)}
            description={`#${detailTarget.companyId}${
              detailTarget.companyNewco?.companyNumber
                ? ` · Co. no. ${detailTarget.companyNewco.companyNumber}`
                : ""
            } · ${summarizeAnswers(detailTarget.answers)}`}
          >
            {detailTarget.dudaStatus === "failed" && detailTarget.dudaError && (
              <div className="error-box">{detailTarget.dudaError}</div>
            )}

            <div className="meta-row">
              <span className="meta-row-label">Status</span>
              <span className="meta-row-value">
                <StatusPill status={detailTarget.dudaStatus} />
              </span>
            </div>
            <div className="meta-row">
              <span className="meta-row-label">Website</span>
              <span className="meta-row-value">
                {detailTarget.websiteUrl ? (
                  <a
                    href={detailTarget.websiteUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      color: "var(--accent-mid)",
                      textDecoration: "underline",
                    }}
                  >
                    View website
                  </a>
                ) : (
                  "-"
                )}
              </span>
            </div>
            <div className="meta-row">
              <span className="meta-row-label">Attempts</span>
              <span className="meta-row-value">{detailTarget.attempts}</span>
            </div>
            <div className="meta-row">
              <span className="meta-row-label">Submitted</span>
              <span className="meta-row-value">
                {formatDate(detailTarget.createdAt)}
              </span>
            </div>
            <div className="meta-row">
              <span className="meta-row-label">Last attempt</span>
              <span className="meta-row-value">
                {formatDate(detailTarget.lastAttemptAt)}
              </span>
            </div>
            <div className="meta-row">
              <span className="meta-row-label">Email</span>
              <span className="meta-row-value">
                {getContactEmail(detailTarget) || "-"}
              </span>
            </div>
            <div className="meta-row">
              <span className="meta-row-label">Phone</span>
              <span className="meta-row-value">
                {getContactPhone(detailTarget) || "-"}
              </span>
            </div>
            {detailTarget.customer?.name && (
              <div className="meta-row">
                <span className="meta-row-label">Customer</span>
                <span className="meta-row-value">
                  {detailTarget.customer.name}
                </span>
              </div>
            )}

            {/* Uploads */}
            {detailLoading ? (
              <div style={{ padding: "12px 0" }}>
                <span
                  style={{
                    fontFamily: "var(--mono)",
                    fontSize: "11px",
                    color: "var(--text-faint)",
                  }}
                >
                  Loading uploads…
                </span>
              </div>
            ) : (
              detailTarget.uploads?.length > 0 && (
                <div className="uploads-section">
                  <div
                    className="meta-row-label"
                    style={{ marginBottom: "8px" }}
                  >
                    Uploads ({detailTarget.uploads.length})
                  </div>
                  <div className="uploads-grid">
                    {detailTarget.uploads.map((u) => (
                      <a
                        key={u.id}
                        href={uploadUrl(u.id)}
                        target="_blank"
                        rel="noreferrer"
                        className="upload-thumb"
                        title={`${u.kind}${u.mimeType ? ` · ${u.mimeType}` : ""}`}
                      >
                        {isImageUpload(u) ? (
                          <img src={uploadUrl(u.id)} alt={u.kind} />
                        ) : (
                          <div className="upload-thumb-file">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M14 2v6h6"
                              />
                            </svg>
                          </div>
                        )}
                        <span className="upload-thumb-label">{u.kind}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )
            )}

            {/* ===== FULL ANSWERS DISPLAY ===== */}
            {!detailLoading &&
              detailTarget.answers &&
              (() => {
                const categorized = categorizeAnswers(detailTarget.answers);
                const totalAnswers = Object.values(categorized).reduce(
                  (sum, arr) => sum + arr.length,
                  0,
                );

                if (totalAnswers === 0) return null;

                return (
                  <div className="answers-full-section">
                    <div className="answers-full-header">
                      <span
                        style={{
                          fontFamily: "var(--mono)",
                          fontSize: "10.5px",
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          color: "var(--text-faint)",
                        }}
                      >
                        All Answers ({totalAnswers})
                      </span>
                    </div>

                    {Object.entries(categorized).map(([category, items]) => (
                      <div key={category} className="answer-category">
                        <div className="answer-category-title">{category}</div>
                        {items.map((item) => (
                          <div className="answer-item-full" key={item.key}>
                            <div className="answer-item-full-key">
                              {item.label}
                            </div>
                            {renderAnswerValue(item.key, item.value)}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                );
              })()}

            <div className="qa-modal-actions">
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setDetailTarget(null)}
              >
                Close
              </button>
              {detailTarget.dudaStatus !== "building" && (
                <button
                  className="btn btn-success-ghost btn-sm"
                  disabled={busyId === detailTarget.id}
                  onClick={() => handleRetry(detailTarget.id)}
                >
                  {busyId === detailTarget.id
                    ? "Retrying…"
                    : detailTarget.dudaStatus === "failed"
                      ? "Retry build"
                      : "Rebuild"}
                </button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
